import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { supabase, getAccessToken } from '../lib/supabase';
import { API } from '../config/api';
import { setSentryUser, clearSentryUser } from '../lib/sentry';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Check if Supabase is configured
const SUPABASE_ENABLED = !!(process.env.REACT_APP_SUPABASE_URL && process.env.REACT_APP_SUPABASE_ANON_KEY);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [supabaseUser, setSupabaseUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backendReady, setBackendReady] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [subscriptionAccess, setSubscriptionAccess] = useState({
    subscription_tier: null,
    subscription_level: 0,
    subscription_name: 'Guest',
    page_access: {},
    is_military: false
  });
  const [squidTourStatus, setSquidTourStatus] = useState({
    completed: true,
    started: false
  });
  const [ndaStatus, setNdaStatus] = useState({
    needsNda: false,
    template: null
  });
  const [lastFetch, setLastFetch] = useState(null);
  const [authError, setAuthError] = useState(null);
  
  // Cache duration - 30 seconds
  const CACHE_DURATION = 30000;
  
  // Session timeout - 30 minutes of inactivity
  const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes in ms
  const lastActivityRef = React.useRef(Date.now());
  
  // Track user activity
  useEffect(() => {
    const updateActivity = () => {
      lastActivityRef.current = Date.now();
    };
    
    // Events that indicate user activity
    const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    
    activityEvents.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
    });
    
    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
    };
  }, []);
  
  // Check for session timeout
  useEffect(() => {
    if (!user) return;
    
    const checkSessionTimeout = () => {
      const timeSinceActivity = Date.now() - lastActivityRef.current;
      if (timeSinceActivity > SESSION_TIMEOUT) {
        console.log('[Auth] Session timeout - logging out due to inactivity');
        // Clear all auth state
        localStorage.removeItem('auth_token');
        localStorage.removeItem('supabase_auth_token');
        sessionStorage.removeItem('cached_user');
        sessionStorage.removeItem('subscription_access');
        sessionStorage.removeItem('squidtour_status');
        setUser(null);
        setSupabaseUser(null);
        // Redirect to login
        window.location.href = '/login?timeout=true';
      }
    };
    
    // Check every minute
    const interval = setInterval(checkSessionTimeout, 60 * 1000);
    
    return () => clearInterval(interval);
  }, [user]);

  // Get auth headers - prefers Supabase token, falls back to legacy
  const getAuthHeaders = useCallback(async (retries = 2) => {
    // Try Supabase session first (primary auth method)
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        // Check if token is close to expiry (less than 5 minutes)
        const expiresAt = session.expires_at;
        const now = Math.floor(Date.now() / 1000);
        const fiveMinutes = 5 * 60;
        
        if (expiresAt && (expiresAt - now) < fiveMinutes) {
          console.log('[Auth] Token close to expiry, attempting refresh...');
          try {
            const { data: refreshData } = await supabase.auth.refreshSession();
            if (refreshData?.session?.access_token) {
              console.log('[Auth] Token refreshed successfully');
              return { Authorization: `Bearer ${refreshData.session.access_token}` };
            }
          } catch (refreshErr) {
            console.warn('[Auth] Token refresh failed:', refreshErr.message);
            // Fall through to use current token
          }
        }
        
        return { Authorization: `Bearer ${session.access_token}` };
      }
    } catch (err) {
      console.log('[Auth] Error getting Supabase session:', err.message);
      if (retries > 0) {
        await new Promise(resolve => setTimeout(resolve, 500));
        return getAuthHeaders(retries - 1);
      }
    }
    
    // Fallback: Check for legacy JWT token in localStorage
    // This supports users who were recovered from Stripe or have MongoDB-only accounts
    const legacyToken = localStorage.getItem('auth_token');
    if (legacyToken) {
      console.log('[Auth] Using legacy JWT token from localStorage');
      return { Authorization: `Bearer ${legacyToken}` };
    }
    
    // No valid session found
    console.log('[Auth] No Supabase session available for API call');
    return {};
  }, []);
  
  // Warmup backend on initial load - this runs ONCE immediately
  // Backend warmup - runs once on mount
  const warmupRef = React.useRef(false);
  
  useEffect(() => {
    if (warmupRef.current) return; // Prevent duplicate in React.StrictMode
    warmupRef.current = true;
    
    const warmupBackend = async () => {
      const maxRetries = 3;
      for (let i = 0; i < maxRetries; i++) {
        try {
          await axios.get(`${API}/ping`, { timeout: 5000 });
          setBackendReady(true);
          console.log('[Auth] Backend warmed up successfully');
          return;
        } catch (err) {
          console.log(`[Auth] Warmup attempt ${i + 1}/${maxRetries} failed, retrying...`);
          await new Promise(r => setTimeout(r, 1000));
        }
      }
      // Even if warmup fails, mark as ready so app doesn't hang
      setBackendReady(true);
    };
    warmupBackend();
  }, []);

  // Token migration: Automatically migrate old 'token' key to 'auth_token'
  // This ensures users logged in with the old key continue to work seamlessly
  useEffect(() => {
    const migrateTokenKey = () => {
      const oldToken = localStorage.getItem('token');
      const newToken = localStorage.getItem('auth_token');
      const sessionToken = sessionStorage.getItem('token');
      
      // If user has old 'token' key but not 'auth_token', migrate it
      if (oldToken && !newToken) {
        console.log('[Auth] Migrating token from "token" to "auth_token"');
        localStorage.setItem('auth_token', oldToken);
        localStorage.removeItem('token');
      }
      
      // Also check sessionStorage
      if (sessionToken && !newToken && !oldToken) {
        console.log('[Auth] Migrating session token to auth_token');
        localStorage.setItem('auth_token', sessionToken);
        sessionStorage.removeItem('token');
      }
    };
    
    migrateTokenKey();
  }, []);

  // Check NDA status for Core Group members
  const checkNdaStatus = useCallback(async () => {
    try {
      const headers = await getAuthHeaders();
      if (!headers.Authorization) {
        setNdaStatus({ needsNda: false, template: null });
        return;
      }
      
      const response = await axios.get(`${API}/nda/check`, {
        headers,
        withCredentials: true
      });
      
      if (response.data.needs_nda) {
        setNdaStatus({
          needsNda: true,
          template: response.data.template
        });
      } else {
        setNdaStatus({ needsNda: false, template: null });
      }
    } catch (error) {
      console.error('Error checking NDA status:', error);
      setNdaStatus({ needsNda: false, template: null });
    }
  }, [getAuthHeaders]);

  // Guard to prevent duplicate fetches
  const fetchInProgressRef = React.useRef(false);
  const lastFetchAttemptRef = React.useRef(0);
  const lastFetchRef = React.useRef(null); // Use ref for cache timing to avoid dependency
  const userRef = React.useRef(null); // Ref to current user for cache check
  
  // Keep refs in sync with state
  useEffect(() => {
    userRef.current = user;
  }, [user]);
  
  useEffect(() => {
    lastFetchRef.current = lastFetch;
  }, [lastFetch]);

  const fetchUserData = useCallback(async (force = false, accessToken = null) => {
    // Debounce: prevent multiple calls within 1 second (EXCEPT when force=true)
    const now = Date.now();
    if (!force && now - lastFetchAttemptRef.current < 1000) {
      console.log('[Auth] Skipping duplicate fetchUserData call (debounced)');
      // CRITICAL: Still set loading=false when skipping to prevent infinite loading
      setLoading(false);
      return;
    }
    lastFetchAttemptRef.current = now;
    
    // Prevent overlapping requests (EXCEPT when force=true - cancel previous)
    if (fetchInProgressRef.current && !force) {
      console.log('[Auth] Skipping duplicate fetchUserData call (already in progress)');
      // Don't set loading=false here - the in-progress request will do it
      return;
    }
    
    // Use cached data if available and not expired (use refs to avoid dependency issues)
    const currentLastFetch = lastFetchRef.current;
    const currentUser = userRef.current;
    if (!force && currentLastFetch && (now - currentLastFetch) < CACHE_DURATION && currentUser) {
      console.log('[Auth] Using cached user data');
      // CRITICAL: Set loading=false when using cache to prevent infinite loading
      setLoading(false);
      return;
    }
    
    fetchInProgressRef.current = true;

    try {
      // If accessToken is provided directly (from auth state change), use it
      // Otherwise, fetch it via getAuthHeaders
      let headers;
      if (accessToken) {
        headers = { Authorization: `Bearer ${accessToken}` };
        console.log('[Auth] Using provided access token for API call');
      } else {
        headers = await getAuthHeaders();
      }
      
      // If no headers (no token), don't make the API call
      if (!headers.Authorization) {
        console.log('[Auth] No authorization token available, skipping fetchUserData');
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${API}/auth/init`, { 
        headers,
        params: { ref: localStorage.getItem('pending_referral') || undefined },
        withCredentials: true,
        timeout: 5000 // 5 second timeout to prevent hanging
      });
      
      const data = response.data;
      
      // Clear pending referral code after it's been sent
      localStorage.removeItem('pending_referral');
      
      setUser(data.user);
      // Set Sentry user context for error tracking
      if (data.user) {
        setSentryUser(data.user);
      }
      setCartCount(data.cart_count || 0);
      setSubscriptionAccess(data.subscription_access || {
        subscription_tier: null,
        subscription_level: 0,
        subscription_name: 'Guest',
        page_access: {},
        is_military: false
      });
      
      // UPDATED Jan 2026: SquidTour SUSPENDED - always set as completed
      setSquidTourStatus({
        completed: true,  // Always true - tour is suspended
        started: true     // Always true - tour is suspended
      });
      
      // Cache in sessionStorage for other components
      sessionStorage.setItem('squidtour_status', 'complete');  // Always complete - tour suspended
      sessionStorage.setItem('subscription_access', JSON.stringify(data.subscription_access));
      sessionStorage.setItem('cached_user', JSON.stringify(data.user));
      
      setLastFetch(now);
      setAuthError(null);
      
      // Check NDA status for Core Group members
      if (data.user?.user_group === 'core_group') {
        await checkNdaStatus();
      } else {
        setNdaStatus({ needsNda: false, template: null });
      }
    } catch (error) {
      // Only clear user on explicit 401 Unauthorized
      // Network errors or server errors should NOT log out the user
      if (error.response?.status === 401) {
        setUser(null);
        clearSentryUser(); // Clear Sentry context on auth failure
        setSquidTourStatus({ completed: true, started: false }); // Guests bypass tour
        setNdaStatus({ needsNda: false, template: null });
        sessionStorage.removeItem('cached_user');
        localStorage.removeItem('auth_token');
      } else {
        // Keep user logged in with cached data on network/server errors
        console.warn('Auth init failed (keeping session):', error.message);
        const cachedUser = sessionStorage.getItem('cached_user');
        if (cachedUser && !userRef.current) {
          try {
            setUser(JSON.parse(cachedUser));
          } catch {}
        }
      }
    } finally {
      fetchInProgressRef.current = false;
      setLoading(false);
    }
  }, [checkNdaStatus, getAuthHeaders]); // REMOVED lastFetch and user from deps - now using refs

  // Initialize Supabase auth listener (if enabled)
  const supabaseInitRef = React.useRef(false);
  const supabaseLoadingSetRef = React.useRef(false); // Track if we've set loading from Supabase
  
  useEffect(() => {
    if (!SUPABASE_ENABLED) return;
    if (supabaseInitRef.current) return; // Prevent duplicate init in React.StrictMode
    supabaseInitRef.current = true;
    
    let mounted = true;
    
    const initSupabaseAuth = async () => {
      console.log('[Auth] Starting Supabase auth init...');
      try {
        const { data: { session } } = await supabase.auth.getSession();
        console.log('[Auth] Supabase getSession completed, session:', session ? 'exists' : 'null');
        
        if (session?.user) {
          if (mounted) {
            setSupabaseUser(session.user);
            console.log('[Auth] Supabase session found, fetching user data');
            if (session.access_token) {
              await fetchUserData(true, session.access_token);
            }
          }
        } else {
          // No Supabase session - set loading to false for unauthenticated users
          // Use ref to ensure this only runs once even with StrictMode remounts
          if (!supabaseLoadingSetRef.current) {
            supabaseLoadingSetRef.current = true;
            console.log('[Auth] No Supabase session, setting loading to false');
            setLoading(false);
          }
        }
      } catch (err) {
        console.warn('[Auth] Supabase init error:', err);
        // On error, also set loading to false to prevent infinite loading
        if (!supabaseLoadingSetRef.current) {
          supabaseLoadingSetRef.current = true;
          console.log('[Auth] Setting loading to false after Supabase error');
          setLoading(false);
        }
      }
    };
    
    initSupabaseAuth();
    
    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return;
        
        console.log('[Auth] Supabase state change:', event);
        
        // Skip INITIAL_SESSION as we handle it in initSupabaseAuth
        if (event === 'INITIAL_SESSION') {
          return;
        }
        
        if (event === 'SIGNED_IN' && session?.user) {
          setSupabaseUser(session.user);
          // Pass the access token directly to avoid race condition with getSession()
          if (session.access_token) {
            console.log('[Auth] Using session token directly from auth state change');
            await fetchUserData(true, session.access_token);
          } else {
            await fetchUserData(true);
          }
        } else if (event === 'SIGNED_OUT') {
          setSupabaseUser(null);
          setUser(null);
          sessionStorage.removeItem('cached_user');
        } else if (event === 'TOKEN_REFRESHED' && session?.access_token) {
          // Token was refreshed, update user data with new token
          console.log('[Auth] Token refreshed, updating user data');
          await fetchUserData(true, session.access_token);
        }
      }
    );
    
    return () => {
      mounted = false;
      subscription?.unsubscribe();
      // DO NOT reset refs here - they should persist across unmount/remount cycles
      // Resetting them causes infinite loops when React StrictMode double-renders
      // or when the component updates frequently
    };
  }, []);

  // Initial fetch - only for non-Supabase (legacy/localStorage) auth
  const initialFetchRef = React.useRef(false);
  
  useEffect(() => {
    if (initialFetchRef.current) return; // Prevent duplicate in React.StrictMode
    initialFetchRef.current = true;
    
    // Check for localStorage auth (from MongoDB fallback) first - this takes priority
    const localUser = localStorage.getItem('user');
    const localToken = localStorage.getItem('auth_token');
    
    if (localUser && localToken) {
      try {
        const userData = JSON.parse(localUser);
        console.log('[Auth] Using localStorage user data (MongoDB/legacy auth)');
        setUser(userData);
        setLoading(false);
        // Validate with backend in background
        fetchUserData(false);
        return;
      } catch {
        // Invalid localStorage data, clear it
        localStorage.removeItem('user');
      }
    }
    
    // If Supabase is enabled, let the Supabase useEffect handle auth
    // Only handle sessionStorage cache here for instant UI
    if (SUPABASE_ENABLED) {
      // Just check for cached user for instant UI, don't trigger API calls
      const cachedUser = sessionStorage.getItem('cached_user');
      if (cachedUser) {
        try {
          const userData = JSON.parse(cachedUser);
          setUser(userData);
          const cachedAccess = sessionStorage.getItem('subscription_access');
          if (cachedAccess) setSubscriptionAccess(JSON.parse(cachedAccess));
          setLoading(false);
        } catch {
          // Invalid cache - clear it and let Supabase useEffect handle auth
          sessionStorage.removeItem('cached_user');
          // Don't set loading=false here - Supabase useEffect will do it
        }
      }
      // Note: Supabase useEffect will set loading=false for all users
      return;
    }
    
    // Non-Supabase flow: Check for cached user first
    const cachedUser = sessionStorage.getItem('cached_user');
    
    // First priority: sessionStorage cache
    if (cachedUser) {
      try {
        const userData = JSON.parse(cachedUser);
        setUser(userData);
        const cachedAccess = sessionStorage.getItem('subscription_access');
        if (cachedAccess) setSubscriptionAccess(JSON.parse(cachedAccess));
        const cachedTour = sessionStorage.getItem('squidtour_status');
        if (cachedTour) setSquidTourStatus({ completed: cachedTour === 'complete', started: true });
        setLoading(false); // Immediately set loading false with cached data
        // Then refresh in background
        fetchUserData(false);
        return;
      } catch {
        // Invalid cache, fetch fresh
      }
    }
    
    // No cache - fetch fresh
    fetchUserData(true);
    
    // DO NOT reset ref in cleanup - it should persist
  }, []);

  // Midnight streak refresh timer
  useEffect(() => {
    const scheduleRefresh = () => {
      const now = new Date();
      const midnight = new Date(now);
      midnight.setHours(24, 0, 5, 0); // 5 seconds past midnight
      const ms = midnight.getTime() - now.getTime();
      return setTimeout(() => {
        if (userRef.current) {
          console.log('[Auth] Midnight streak refresh');
          fetchUserData(true);
        }
        // Schedule next one
        scheduleRefresh();
      }, ms);
    };
    const tid = scheduleRefresh();
    return () => clearTimeout(tid);
  }, [fetchUserData]);

  // Login - Supabase ONLY (unified auth)
  const login = useCallback(async (email, password) => {
    setAuthError(null);
    
    if (!SUPABASE_ENABLED) {
      setAuthError('Authentication service is not available');
      throw new Error('Authentication service is not available');
    }
    
    try {
      // Try Supabase login
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (!error && data.session) {
        console.log('[Auth] Supabase login successful, fetching user data...');
        
        // Fetch user data with Supabase token
        const token = data.session.access_token;
        const response = await axios.get(`${API}/auth/init`, { 
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true 
        });
        
        if (response.data.user) {
          setUser(response.data.user);
          setCartCount(response.data.cart_count || 0);
          setSubscriptionAccess(response.data.subscription_access || {
            subscription_tier: null,
            subscription_level: 0,
            subscription_name: 'Guest',
            page_access: {},
            is_military: false
          });
          sessionStorage.setItem('cached_user', JSON.stringify(response.data.user));
          setLoading(false);
          return { success: true, user: data.user, provider: 'supabase' };
        }
      }
      
      // If Supabase login failed, try fallback to MongoDB backend
      // Handle various error types: "Invalid login credentials", "body stream already read", or any auth error
      if (error) {
        const errorMessage = error.message || '';
        const shouldTryFallback = errorMessage.includes('Invalid login credentials') || 
                                   errorMessage.includes('body stream already read') ||
                                   errorMessage.includes('AuthUnknownError') ||
                                   error.status === 400 ||
                                   error.__isAuthError;
        
        if (shouldTryFallback) {
          console.log('[Auth] Supabase login failed, trying MongoDB backend fallback...');
          
          try {
            // Try MongoDB backend login
            const backendResponse = await axios.post(`${API}/auth/login`, { 
              email, 
              password 
            });
            
            if (backendResponse.data.token && backendResponse.data.user) {
              console.log('[Auth] MongoDB backend login successful');
              
              // Store JWT token for future requests
              localStorage.setItem('auth_token', backendResponse.data.token);
              localStorage.setItem('user', JSON.stringify(backendResponse.data.user));
              
              setUser(backendResponse.data.user);
              setLoading(false);
              
              // Trigger a page reload to pick up the new token
              window.location.href = '/blackboard';
              return { success: true, user: backendResponse.data.user, provider: 'mongodb' };
            }
          } catch (backendErr) {
            console.log('[Auth] MongoDB backend login failed:', backendErr.message);
          
            // Check if this email exists and needs password reset
            try {
              const checkResponse = await axios.post(`${API}/auth/check-migration-status`, { email });
              
              if (checkResponse.data.needs_password_reset) {
                setAuthError('Please reset your password to continue. Check your email for instructions.');
                return { 
                  success: false, 
                  needsPasswordReset: true, 
                  message: 'Please reset your password to continue. Check your email for instructions.'
                };
              }
            } catch (checkErr) {
              console.log('[Auth] Migration check failed:', checkErr.message);
            }
          }
        }
        
        setAuthError('Invalid email or password. If you haven\'t logged in recently, please use "Forgot Password" to reset your credentials.');
        throw new Error('Invalid email or password');
      }
      
      if (error) {
        setAuthError(error.message);
        throw error;
      }
      
    } catch (err) {
      if (!err.message.includes('Invalid')) {
        setAuthError(err.response?.data?.detail || err.message || 'Login failed');
      }
      throw err;
    }
  }, []);

  // Sign up with Supabase (unified auth)
  const signUp = useCallback(async (email, password, name, ref) => {
    setAuthError(null);
    
    if (!SUPABASE_ENABLED) {
      setAuthError('Authentication service is not available');
      throw new Error('Authentication service is not available');
    }
    
    // Store referral code for session exchange
    if (ref) {
      localStorage.setItem('pending_referral', ref);
    }
    
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: name,
            name: name
          }
        }
      });

      if (error) throw error;

      // If email confirmation is disabled, user is immediately signed in
      if (data.session) {
        const token = data.session.access_token;
        await fetchUserData(true, token);
      }

      return { success: true, data, needsConfirmation: !data.session };
    } catch (err) {
      setAuthError(err.message);
      throw err;
    }
  }, [fetchUserData]);

  // Sign in with Google via Supabase
  const signInWithGoogle = useCallback(async () => {
    if (!SUPABASE_ENABLED) {
      throw new Error('Supabase is not configured for Google login');
    }
    
    // Preserve referral code through OAuth redirect
    const urlParams = new URLSearchParams(window.location.search);
    const ref = urlParams.get('ref');
    if (ref) {
      localStorage.setItem('pending_referral', ref);
    }
    
    setAuthError(null);
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      });

      if (error) throw error;
      return data;
    } catch (err) {
      setAuthError(err.message);
      throw err;
    }
  }, []);

  // Password reset via Supabase
  const resetPassword = useCallback(async (email) => {
    if (!SUPABASE_ENABLED) {
      throw new Error('Supabase is not configured for password reset');
    }
    
    setAuthError(null);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });

      if (error) throw error;
      return { success: true };
    } catch (err) {
      setAuthError(err.message);
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    // CRITICAL: Clear local state FIRST before any async operations
    // This ensures logout works even if network calls fail
    
    // 1. Clear all local storage immediately
    localStorage.removeItem('auth_token');
    localStorage.removeItem('supabase_auth_token');
    sessionStorage.removeItem('cached_user');
    sessionStorage.removeItem('subscription_access');
    sessionStorage.removeItem('squidtour_status');
    
    // 2. Clear React state immediately
    setUser(null);
    setSupabaseUser(null);
    setCartCount(0);
    setSubscriptionAccess({
      subscription_tier: null,
      subscription_level: 0,
      subscription_name: 'Guest',
      page_access: {},
      is_military: false
    });
    setSquidTourStatus({ completed: true, started: false });
    setNdaStatus({ needsNda: false, template: null });
    
    // Clear Sentry user context
    clearSentryUser();
    
    // 3. Now try to sign out from external services (non-blocking)
    // These are best-effort - local logout is already complete
    try {
      if (SUPABASE_ENABLED) {
        const signOutPromise = supabase.auth.signOut();
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('timeout')), 3000)
        );
        await Promise.race([signOutPromise, timeoutPromise]);
      }
    } catch (e) {
      // Silently ignore - local logout already complete
    }
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);
      await axios.post(`${API}/auth/logout`, {}, { 
        withCredentials: true,
        signal: controller.signal 
      });
      clearTimeout(timeoutId);
    } catch (e) {
      // Silently ignore - local logout already complete
    }
  }, []);

  // Store fetchUserData in a ref to avoid effect re-runs when it changes
  const fetchUserDataRef = React.useRef(fetchUserData);
  useEffect(() => {
    fetchUserDataRef.current = fetchUserData;
  }, [fetchUserData]);

  const refreshUser = useCallback(() => {
    // Clear cache to ensure fresh data
    sessionStorage.removeItem('cached_user');
    setLastFetch(null);
    return fetchUserDataRef.current(true);
  }, []); // Stable identity - uses ref

  // Listen for userUpdated events (from admin panel, etc.)
  // FIXED: Use ref to avoid re-subscribing when fetchUserData changes
  useEffect(() => {
    const handleUserUpdated = () => {
      console.log('User data updated event received, refreshing...');
      // Clear cache and force refresh
      sessionStorage.removeItem('cached_user');
      setLastFetch(null);
      fetchUserDataRef.current(true);
    };
    
    const handleStorageChange = (e) => {
      if (e.key === 'user_coins_updated') {
        handleUserUpdated();
      }
    };
    
    window.addEventListener('userUpdated', handleUserUpdated);
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('userUpdated', handleUserUpdated);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []); // Empty deps - handlers use refs
  
  // Periodic refresh of user data every 30 seconds to keep XP/stats in sync
  // FIXED: Only depend on user existence, not fetchUserData identity
  useEffect(() => {
    if (!user) return;
    
    const interval = setInterval(() => {
      fetchUserDataRef.current(true);
    }, 30000); // Refresh every 30 seconds
    
    return () => clearInterval(interval);
  }, [user]); // Only re-run when user login/logout status changes

  const updateCartCount = useCallback((count) => {
    setCartCount(count);
  }, []);

  // Expose a method to force refresh user data (including squidtour status)
  const refreshUserData = useCallback(() => {
    return fetchUserDataRef.current(true);
  }, []); // Stable identity - uses ref

  // Clear NDA requirement after signing
  const clearNdaRequirement = useCallback(() => {
    setNdaStatus({ needsNda: false, template: null });
  }, []);

  const value = {
    user,
    supabaseUser,
    loading,
    cartCount,
    subscriptionAccess,
    squidTourStatus,
    ndaStatus,
    authError,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin' || user?.role === 'super_admin',
    isCoreGroup: user?.user_group === 'core_group',
    isSupabaseEnabled: SUPABASE_ENABLED,
    // Auth methods
    login,
    signUp,
    logout,
    signInWithGoogle,
    resetPassword,
    // Utility methods  
    refreshUser,
    updateCartCount,
    refreshUserData,
    clearNdaRequirement,
    checkNdaStatus,
    getAuthHeaders
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
